import React, { useState } from 'react';
import type { TravelNodeData } from '../../types';
import type { ProposalRateSettings } from '../../utils/appSettings';
import { isMappedInstallLaborPerson, isOthersInstallLaborLine } from '../../utils/proposalInstallLabor';
import {
  isHotelNightsAutocalculated,
  listTravelDayPeople,
  travelDayPeopleRate,
  travelDaysTotal,
  travelHotelRooms,
  travelHotelTotal,
  travelMileageTotal,
  travelPerDiemPersonQuantity,
  travelPerDiemPersonTotal,
  travelPerDiemTotal,
  travelPersonDayTotal,
  travelPersonQuantity,
  type TravelLaborLine,
} from '../../utils/proposalTravelTotals';
import {
  structureCompareRowProps,
  type StructureLineCompareMap,
} from '../../utils/structureLineCompare';
import './HardwareListTable.css';
import './ProposalBreakdownGroups.css';
import './InstallBreakdownGroups.css';

const GroupTitle: React.FC<{ label: string; extra?: React.ReactNode }> = ({ label, extra }) => (
  <div className="hardware-group-title">
    <span>{label}</span>
    {extra}
  </div>
);

interface TravelBreakdownGroupsProps {
  travel: TravelNodeData;
  rates: ProposalRateSettings;
  installDays?: number;
  laborLines?: TravelLaborLine[];
  disabled?: boolean;
  formatCurrency: (value: number) => string;
  onTravelChange: (patch: Partial<TravelNodeData>) => void;
  onBlur?: () => void;
  compareFields?: StructureLineCompareMap;
}

const TravelBreakdownGroups: React.FC<TravelBreakdownGroupsProps> = ({
  travel,
  rates,
  installDays = 0,
  laborLines,
  disabled,
  formatCurrency,
  onTravelChange,
  onBlur,
  compareFields,
}) => {
  const [hotelQtyTip, setHotelQtyTip] = useState(false);
  const readonly = disabled ? ' hardware-table--readonly' : '';
  const mileageTotal = travelMileageTotal(travel, rates);
  const hotelTotal = travelHotelTotal(travel);
  const perDiemTotal = travelPerDiemTotal(travel, laborLines);
  const people = listTravelDayPeople(laborLines);
  const daysTotal = travelDaysTotal(travel, rates, laborLines);
  const fallbackRate = travelDayPeopleRate(rates, laborLines);
  const formatRate = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);

  const numInput = (
    value: number,
    field: keyof TravelNodeData,
    step = '1',
    title?: string,
  ) => (
    <input
      type="number"
      min="0"
      step={step}
      value={value || ''}
      disabled={disabled}
      title={title}
      onChange={(e) => onTravelChange({ [field]: parseFloat(e.target.value) || 0 })}
      onBlur={onBlur}
    />
  );

  return (
    <div className="timber-list-container hardware-list-wrap">
      <div className="hardware-group" aria-label="Vehicles">
        <GroupTitle label="Vehicles" />
        <table className={`hardware-table hardware-table--qty-cost${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Name</th>
              <th className="hardware-col-num">Qty</th>
              <th className="hardware-col-num">Rate</th>
              <th className="hardware-col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr {...structureCompareRowProps(compareFields, 'travel.miles')}>
              <td className="hardware-col-name">
                <span className="hardware-ref-text">Mileage</span>
                <span className="hardware-ref-sub">Round trip (one-way × 2)</span>
              </td>
              <td className="hardware-col-num">
                {numInput(travel.milesToJobSite, 'milesToJobSite', '1', 'One-way miles')}
              </td>
              <td className="hardware-col-num">
                <span title="Per-mile rate × 2 for the return trip">
                  {formatRate(rates.mileageRate)} × 2
                </span>
              </td>
              <td className="hardware-col-num">
                <span>{formatCurrency(mileageTotal)}</span>
              </td>
            </tr>
            <tr {...structureCompareRowProps(compareFields, 'travel.fuel')}>
              <td className="hardware-col-name">
                <span className="hardware-ref-text">Fuel</span>
              </td>
              <td className="hardware-col-num" />
              <td className="hardware-col-num" />
              <td className="hardware-col-num">{numInput(travel.fuelBudget, 'fuelBudget', '0.01')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="hardware-group" aria-label="Paid Travel Days">
        <GroupTitle
          label="Paid Travel Days"
          extra={(
            <label
              className={['install-days-field', 'install-days-field--onsite', structureCompareRowProps(compareFields, 'travel.days').className].filter(Boolean).join(' ')}
              title={structureCompareRowProps(compareFields, 'travel.days').title || 'Paid days traveling to and from the job, billed at each listed person’s daily rate.'}
            >
              Days
              <input
                type="number"
                min="0"
                step="1"
                value={travel.travelDays || ''}
                disabled={disabled}
                aria-label="Paid travel days"
                onChange={(e) => onTravelChange({ travelDays: Math.max(0, Math.round(parseFloat(e.target.value) || 0)) })}
                onBlur={onBlur}
              />
            </label>
          )}
        />
        <table className={`hardware-table hardware-table--qty-cost${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Employee</th>
              <th className="hardware-col-num">People</th>
              <th className="hardware-col-num">$/day</th>
              <th className="hardware-col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {people.length > 0 ? (
              people.map((line) => {
                const mappedPerson = isMappedInstallLaborPerson(line);
                const others = isOthersInstallLaborLine(line);
                return (
                  <tr
                    key={line.id}
                    className={[others ? 'install-labor-row--others' : '', structureCompareRowProps(compareFields, line.id).className].filter(Boolean).join(' ') || undefined}
                    title={structureCompareRowProps(compareFields, line.id).title}
                  >
                    <td className="hardware-col-name">
                      <div className={`install-labor-person${others ? ' install-labor-person--others' : ''}`}>
                        <span className="hardware-ref-text">{line.role}</span>
                        {others && <span className="install-labor-others-badge">Unassigned</span>}
                      </div>
                    </td>
                    <td className="hardware-col-num">
                      {mappedPerson ? (
                        <span className="install-labor-people-dash" aria-label="One person">—</span>
                      ) : (
                        <span>{travelPersonQuantity(line) || ''}</span>
                      )}
                    </td>
                    <td className="hardware-col-num">
                      <span>{formatCurrency(line.ratePerDay)}</span>
                    </td>
                    <td className="hardware-col-num">
                      <span>{formatCurrency(travelPersonDayTotal(line, travel.travelDays || 0))}</span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="hardware-col-name">
                  <span className="hardware-ref-text">Travel days</span>
                </td>
                <td className="hardware-col-num" />
                <td className="hardware-col-num">
                  <span>{formatCurrency(fallbackRate)}</span>
                </td>
                <td className="hardware-col-num">
                  <span>{formatCurrency(daysTotal)}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {people.length === 0 && (
          <p className="install-days-hint">
            Add people under Installation Labor to bill travel days at each person’s rate.
          </p>
        )}
      </div>

      <div className="hardware-group" aria-label="Hotels">
        <GroupTitle label="Hotels" />
        <table className={`hardware-table hardware-table--qty-cost${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Name</th>
              <th className="hardware-col-num">Rooms</th>
              <th className="hardware-col-num">Qty</th>
              <th className="hardware-col-num">Rate</th>
              <th className="hardware-col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr {...structureCompareRowProps(compareFields, 'travel.hotel')}>
              <td className="hardware-col-name">
                <span className="hardware-ref-text" title="Rooms × nights × rate">
                  Hotel
                </span>
              </td>
              <td className="hardware-col-num">
                {numInput(
                  travelHotelRooms(travel),
                  'hotelRooms',
                  '1',
                  'Number of rooms',
                )}
              </td>
              <td className="hardware-col-num travel-hotel-qty">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={travel.hotelNights || ''}
                  disabled={disabled}
                  aria-label="Hotel nights"
                  aria-describedby={hotelQtyTip ? 'travel-hotel-qty-tip' : undefined}
                  onFocus={() => {
                    if (isHotelNightsAutocalculated(travel.hotelNights, installDays, travel.travelDays)) {
                      setHotelQtyTip(true);
                    }
                  }}
                  onChange={(e) => onTravelChange({ hotelNights: parseFloat(e.target.value) || 0 })}
                  onBlur={() => {
                    setHotelQtyTip(false);
                    onBlur?.();
                  }}
                />
                {hotelQtyTip && (
                  <div id="travel-hotel-qty-tip" className="travel-hotel-qty-pop" role="status">
                    Autocalculated from on-site days + travel days − 1. You can edit this.
                  </div>
                )}
              </td>
              <td className="hardware-col-num">{numInput(travel.hotelRatePerNight, 'hotelRatePerNight', '0.01')}</td>
              <td className="hardware-col-num">
                <span>{formatCurrency(hotelTotal)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="hardware-group" aria-label="Per Diem">
        <GroupTitle
          label="Per Diem"
          extra={(
            <>
              <span
                className="install-days-field install-days-field--onsite"
                title="Per diem is billed for each hotel night."
              >
                {travel.hotelNights || 0} nights
              </span>
              <label
                className={['install-days-field', 'install-days-field--onsite', structureCompareRowProps(compareFields, 'travel.perDiem').className].filter(Boolean).join(' ')}
                title={structureCompareRowProps(compareFields, 'travel.perDiem').title || 'Flat rate per person for each hotel night.'}
              >
                $/person
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={travel.perDiemPerPersonPerDay || ''}
                  disabled={disabled}
                  aria-label="Per diem per person per night"
                  onChange={(e) => onTravelChange({ perDiemPerPersonPerDay: parseFloat(e.target.value) || 0 })}
                  onBlur={onBlur}
                />
              </label>
            </>
          )}
        />
        <table className={`hardware-table hardware-table--qty-cost${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Employee</th>
              <th className="hardware-col-num">People</th>
              <th className="hardware-col-num">$/day</th>
              <th className="hardware-col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {people.length > 0 ? (
              people.map((line) => {
                const mappedPerson = isMappedInstallLaborPerson(line);
                const others = isOthersInstallLaborLine(line);
                const quantity = travelPerDiemPersonQuantity(line, travel);
                return (
                  <tr
                    key={`per-diem-${line.id}`}
                    className={[others ? 'install-labor-row--others' : '', structureCompareRowProps(compareFields, 'travel.perDiem').className, structureCompareRowProps(compareFields, line.id).className].filter(Boolean).join(' ') || undefined}
                    title={structureCompareRowProps(compareFields, 'travel.perDiem').title || structureCompareRowProps(compareFields, line.id).title}
                  >
                    <td className="hardware-col-name">
                      <div className={`install-labor-person${others ? ' install-labor-person--others' : ''}`}>
                        <span className="hardware-ref-text">{line.role}</span>
                        {others && <span className="install-labor-others-badge">Unassigned</span>}
                      </div>
                    </td>
                    <td className="hardware-col-num">
                      {line.id === 'legacy-crew'
                        ? numInput(travel.crewSize, 'crewSize')
                        : mappedPerson ? (
                          <span className="install-labor-people-dash" aria-label="One person">—</span>
                        ) : (
                          <span>{quantity || ''}</span>
                        )}
                    </td>
                    <td className="hardware-col-num">
                      <span>{formatCurrency(travel.perDiemPerPersonPerDay)}</span>
                    </td>
                    <td className="hardware-col-num">
                      <span>{formatCurrency(travelPerDiemPersonTotal(line, travel))}</span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr {...structureCompareRowProps(compareFields, people.length === 0 ? 'travel.crew' : 'travel.perDiem')}>
                <td className="hardware-col-name">
                  <span className="hardware-ref-text">Per diem</span>
                </td>
                <td className="hardware-col-num">{numInput(travel.crewSize, 'crewSize')}</td>
                <td className="hardware-col-num">
                  <span>{formatCurrency(travel.perDiemPerPersonPerDay)}</span>
                </td>
                <td className="hardware-col-num">
                  <span>{formatCurrency(perDiemTotal)}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {people.length === 0 && (
          <p className="install-days-hint">
            Add people under Installation Labor to bill per diem for each person.
          </p>
        )}
      </div>
    </div>
  );
};

export default TravelBreakdownGroups;
